import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { isRoomLanguage, type RoomLanguage } from "@jaxo/realtime/games";

type I18nContextValue = {
  language: RoomLanguage;
  setLanguage: (language: RoomLanguage) => void;
  tr: (italian: string, english: string) => string;
  locale: string;
};

const STORAGE_KEY = "jaxo:language";
const I18nContext = createContext<I18nContextValue | null>(null);

function storedLanguage(): RoomLanguage {
  const requested = new URLSearchParams(location.search).get("lang");
  if (isRoomLanguage(requested)) return requested;
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (isRoomLanguage(value)) return value;
  } catch { /* Lo storage può essere disabilitato dal browser. */ }
  return "it";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<RoomLanguage>(storedLanguage);
  const setLanguage = useCallback((next: RoomLanguage) => {
    setLanguageState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* Preferenza solo per la sessione. */ }
  }, []);
  useEffect(() => { document.documentElement.lang = language; }, [language]);
  const value = useMemo<I18nContextValue>(() => ({
    language,
    setLanguage,
    tr: (italian, english) => language === "it" ? italian : english,
    locale: language === "it" ? "it-IT" : "en-GB"
  }), [language, setLanguage]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useLanguage(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("LanguageProvider is missing");
  return value;
}

export function useRoomLanguage(roomLanguage?: RoomLanguage): void {
  const { setLanguage } = useLanguage();
  useEffect(() => {
    if (roomLanguage) setLanguage(roomLanguage);
  }, [roomLanguage, setLanguage]);
}
